'use client'

import { ErrorMessage, Form, Formik } from 'formik'
import {
  AnimatePresence,
  motion,
  useIsPresent,
  useReducedMotion,
} from 'framer-motion'
import { OTPInput, REGEXP_ONLY_DIGITS, type SlotProps } from 'input-otp'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { useEffect, useId, useRef, useState } from 'react'

import { type VerificationMethod } from '@/features/tfa/store'
import { isHttpError, translateError } from '@/lib/apis/error'
import {
  softEditorialEase,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'

export const TFA_CHALLENGE_PANEL_CLASSES =
  '!max-w-[400px] !rounded-[24px] !border !border-[#ECECEF] bg-white !px-6 !pb-6 !pt-6 !shadow-[0_32px_80px_rgba(11,11,15,0.18)] sm:!p-8'

// A code is always sent before this modal opens, so the first entry is the wait
// the patient starts on. Each resend steps down the list, keeping us inside
// Twilio Verify's send limit for a single verification.
export const DEFAULT_RESEND_COOLDOWN_SCHEDULE = [60, 120, 300] as const

export type TfaChallengeModalProps = {
  step: number
  incrementStep: () => Promise<void>
  steps: Array<VerificationMethod>
  onCancelVerification: () => Promise<void>
  onResendCode: (token: string, step: number) => void
  initialCode?: string
  autoFocus?: boolean
  resendCooldownSchedule?: readonly number[]
}

export default function TfaChallengeModal({
  step,
  incrementStep,
  steps,
  onCancelVerification,
  onResendCode,
  initialCode = '',
  autoFocus = true,
  resendCooldownSchedule = DEFAULT_RESEND_COOLDOWN_SCHEDULE,
}: TfaChallengeModalProps) {
  if (steps.length === 0) return null

  const method = steps[step]

  return (
    <div className="grid">
      <AnimatePresence initial={false} mode="sync">
        <ModalBody
          {...method}
          key={`${method.channel}-${step}`}
          autoFocus={autoFocus}
          initialCode={initialCode}
          onCancelVerification={onCancelVerification}
          onSuccessfulVerification={async (token) => {
            await method.onSuccessfulVerification?.(token)
            await incrementStep()
          }}
          onResendCode={onResendCode}
          resendCooldownSchedule={resendCooldownSchedule}
          step={step}
        />
      </AnimatePresence>
    </div>
  )
}

type ModalBodyProps = VerificationMethod & {
  step: number
  initialCode: string
  autoFocus: boolean
  onCancelVerification: () => Promise<void>
  onResendCode: (token: string, step: number) => void
  resendCooldownSchedule: readonly number[]
}

function ModalBody({
  onCancelVerification,
  channel,
  address,
  verify,
  onSuccessfulVerification,
  resendCode,
  onResendCode,
  step,
  tokenState,
  initialCode,
  autoFocus,
  resendCooldownSchedule,
}: ModalBodyProps) {
  const headingId = useId()
  const descriptionId = useId()
  const errorId = useId()
  const isPresent = useIsPresent()
  const prefersReducedMotion = useReducedMotion()
  const codeInputRef = useRef<HTMLInputElement>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const formattedAddress = formatVerificationAddress(channel, address)
  const isSms = channel === 'Sms'

  const title = isSms ? 'Confirm your mobile' : 'Confirm your email'
  const description = isSms
    ? `The doctor calls you on this number. We've texted a 6-digit code to ${formattedAddress}.`
    : `We've emailed a 6-digit code to ${formattedAddress}. Check your spam folder if it hasn't arrived.`

  return (
    <motion.div
      aria-hidden={isPresent ? undefined : true}
      className="col-start-1 row-start-1"
      initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
      animate={
        prefersReducedMotion
          ? { opacity: 1, y: 0 }
          : { opacity: [0, 0, 1], y: 0 }
      }
      exit={
        prefersReducedMotion ? { opacity: 0 } : { opacity: [1, 0, 0], y: -8 }
      }
      inert={isPresent ? undefined : true}
      transition={{
        duration: prefersReducedMotion ? 0 : softMotionDuration.handoff,
        ease: softEditorialEase,
        opacity: prefersReducedMotion
          ? { duration: 0 }
          : {
              duration: softMotionDuration.handoff,
              ease: softEditorialEase,
              times: isPresent ? [0, 0.18, 1] : [0, 0.38, 1],
            },
      }}
    >
      <Formik
        initialValues={{ code: initialCode }}
        onSubmit={async (
          values,
          { setSubmitting, setValues, setFieldError },
        ) => {
          if (values.code.length !== 6) {
            setFieldError('code', 'Enter the full 6-digit code.')
            setSubmitting(false)
            return
          }

          if (!tokenState.token) {
            setFieldError('code', 'Request a new code and try again.')
            setSubmitting(false)
            return
          }

          try {
            const token = await verify(tokenState.token, values.code)
            await onSuccessfulVerification?.(token)
            await setValues({ code: '' })
          } catch (error) {
            setFieldError('code', verificationErrorMessage(error))
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          isSubmitting,
          setFieldTouched,
          setFieldValue,
          setFieldError,
        }) => {
          const hasError = Boolean(touched.code && errors.code)

          return (
            <Form
              aria-describedby={descriptionId}
              aria-labelledby={headingId}
              className="flex flex-col gap-5"
              noValidate
            >
              <div className="flex flex-col gap-2">
                <h2
                  className="font-jakarta text-[22px] font-bold leading-7 tracking-[-0.01em] text-[#0B0B0F]"
                  id={headingId}
                >
                  {title}
                </h2>
                <p
                  className="font-sans text-[14.5px] leading-[21px] text-[#82828B]"
                  id={descriptionId}
                >
                  {description}
                </p>
              </div>

              <div className="flex flex-col gap-2.5">
                <OTPInput
                  aria-describedby={hasError ? errorId : undefined}
                  aria-invalid={hasError}
                  aria-label="6-digit verification code"
                  autoComplete="one-time-code"
                  autoFocus={autoFocus}
                  containerClassName="w-full"
                  disabled={isSubmitting || isCancelling}
                  inputMode="numeric"
                  maxLength={6}
                  name="code"
                  onBlur={() => void setFieldTouched('code', true)}
                  onChange={(code) => {
                    void setFieldValue('code', code, false)
                    if (errors.code) setFieldError('code', undefined)
                  }}
                  pattern={REGEXP_ONLY_DIGITS}
                  pushPasswordManagerStrategy="none"
                  ref={codeInputRef}
                  render={({ slots }) => (
                    <motion.div
                      aria-hidden="true"
                      animate={
                        hasError && !prefersReducedMotion
                          ? { x: [0, -2, 2, -2, 0] }
                          : { x: 0 }
                      }
                      className="grid w-full max-w-[328px] grid-cols-6 gap-2"
                      transition={{ duration: hasError ? 0.12 : 0 }}
                    >
                      {slots.map((slot, index) => (
                        <VerificationCodeSlot
                          disabled={isSubmitting || isCancelling}
                          hasError={hasError}
                          key={index}
                          {...slot}
                        />
                      ))}
                    </motion.div>
                  )}
                  value={values.code}
                />

                <ErrorMessage name="code">
                  {(message) => (
                    <div
                      className="flex items-start gap-1.5 text-[#C6453C]"
                      id={errorId}
                      role="alert"
                    >
                      <ExclamationCircleIcon
                        aria-hidden="true"
                        className="mt-[1.5px] size-3.5 shrink-0 stroke-2"
                      />
                      <p className="font-sans text-[13.5px] font-medium leading-[18px]">
                        {message}
                      </p>
                    </div>
                  )}
                </ErrorMessage>
              </div>

              <button
                className="flex h-[52px] w-full items-center justify-center rounded-full bg-[#0B0B0F] font-sans text-[14.5px] font-semibold leading-[18px] text-white transition-[background-color,color,opacity] duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:bg-[#24242A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0B0B0F] focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-55"
                disabled={isSubmitting || isCancelling}
                type="submit"
              >
                {isSubmitting ? 'Verifying…' : 'Verify →'}
              </button>

              <div className="flex items-center justify-between gap-4">
                <ResendButton
                  cooldownSchedule={resendCooldownSchedule}
                  loading={isSubmitting || isCancelling}
                  onResendFailed={(message) => {
                    void setFieldTouched('code', true, false)
                    setFieldError('code', message)
                  }}
                  onResent={() => {
                    void setFieldValue('code', '', false)
                    void setFieldTouched('code', false, false)
                    setFieldError('code', undefined)
                    window.requestAnimationFrame(() =>
                      codeInputRef.current?.focus(),
                    )
                  }}
                  resendCode={async () => {
                    const token = await resendCode()
                    onResendCode(token, step)
                  }}
                />
                <button
                  className="shrink-0 font-sans text-[13px] font-medium leading-[17px] text-[#82828B] transition-[color,opacity] duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:text-[#0B0B0F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0B0B0F] focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-50"
                  disabled={isSubmitting || isCancelling}
                  onClick={async () => {
                    setIsCancelling(true)
                    try {
                      await onCancelVerification()
                    } finally {
                      setIsCancelling(false)
                    }
                  }}
                  type="button"
                >
                  {isSms ? 'Wrong number?' : 'Wrong email?'}
                </button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </motion.div>
  )
}

type VerificationCodeSlotProps = SlotProps & {
  disabled: boolean
  hasError: boolean
}

function VerificationCodeSlot({
  char,
  hasFakeCaret,
  isActive,
  disabled,
  hasError,
}: VerificationCodeSlotProps) {
  return (
    <div
      className={[
        'relative flex h-14 min-w-0 items-center justify-center rounded-[14px] border-[1.5px] font-sans text-xl font-semibold leading-6 text-[#0B0B0F]',
        'transition-[background-color,border-color,color,opacity] duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)]',
        hasError
          ? 'border-[#C6453C] bg-white'
          : isActive
            ? 'border-[#0B0B0F] bg-white'
            : 'border-transparent bg-[#F4F4F6]',
        disabled && 'opacity-50',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {char}
      {hasFakeCaret && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="h-[22px] w-0.5 animate-caret-blink bg-[#0B0B0F] motion-reduce:animate-none" />
        </span>
      )}
    </div>
  )
}

type ResendButtonProps = {
  cooldownSchedule: readonly number[]
  resendCode: () => Promise<void>
  loading: boolean
  onResent: () => void
  onResendFailed: (message: string) => void
}

function ResendButton({
  cooldownSchedule,
  resendCode,
  loading,
  onResent,
  onResendFailed,
}: ResendButtonProps) {
  const [resendingCode, setResendingCode] = useState(false)
  const [resendCount, setResendCount] = useState(0)
  const [secondsRemaining, setSecondsRemaining] = useState(() =>
    cooldownForResend(cooldownSchedule, 0),
  )
  const isCoolingDown = secondsRemaining > 0
  const isLoading = loading || resendingCode

  useEffect(() => {
    if (!isCoolingDown) return

    const timer = window.setTimeout(() => {
      setSecondsRemaining((current) => Math.max(current - 1, 0))
    }, 1000)

    return () => window.clearTimeout(timer)
  }, [isCoolingDown, secondsRemaining])

  return (
    <button
      className={[
        'shrink-0 font-sans text-[13px] leading-[17px] transition-[color,opacity] duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2',
        isCoolingDown
          ? 'cursor-default font-normal text-[#A0A0A8] disabled:opacity-100'
          : 'font-medium text-[#2563EB] hover:text-[#1D4ED8] disabled:cursor-wait disabled:opacity-50',
      ].join(' ')}
      disabled={isLoading || isCoolingDown}
      onClick={async () => {
        setResendingCode(true)
        try {
          await resendCode()
          const nextResendCount = resendCount + 1
          setResendCount(nextResendCount)
          setSecondsRemaining(
            cooldownForResend(cooldownSchedule, nextResendCount),
          )
          onResent()
        } catch (error) {
          onResendFailed(resendErrorMessage(error))
        } finally {
          setResendingCode(false)
        }
      }}
      type="button"
    >
      {resendingCode
        ? 'Sending…'
        : isCoolingDown
          ? `Resend in ${formatCountdown(secondsRemaining)}`
          : 'Resend code'}
    </button>
  )
}

// The schedule is indexed by how many resends have happened, so index 0 is the
// wait for the code that was sent before the modal opened. The last entry
// repeats once the patient runs off the end of the list.
function cooldownForResend(
  schedule: readonly number[],
  resendCount: number,
): number {
  if (schedule.length === 0) return 0

  const index = Math.min(resendCount, schedule.length - 1)
  return Math.max(schedule[index], 0)
}

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

// The backend answers a bad code with TFA_FAILURE and a stale or mismatched
// challenge with INVALID_TOKEN. Everything else — Twilio rate limits, expired
// sessions, outages — carries its own message and must keep it, or patients
// retry a correct code against an error that has nothing to do with the code.
function verificationErrorMessage(error: unknown): string {
  if (isHttpError(error)) {
    const code = error.response?.error

    return code === 'TFA_FAILURE' || code === 'INVALID_TOKEN'
      ? "That code doesn't match."
      : translateError(error)
  }

  console.error('Unexpected two-factor verification error', error)
  return "We couldn't check that code. Try again in a moment."
}

function resendErrorMessage(error: unknown): string {
  if (isHttpError(error)) return translateError(error)

  console.error('Unexpected two-factor resend error', error)
  return "We couldn't send a new code. Try again in a moment."
}

function formatVerificationAddress(
  channel: VerificationMethod['channel'],
  address: string,
) {
  if (channel !== 'Sms') return address

  const digits = address.replace(/\D/g, '')
  let localNumber = digits

  if (digits.startsWith('61') && digits.length === 11) {
    localNumber = `0${digits.slice(2)}`
  } else if (digits.startsWith('4') && digits.length === 9) {
    localNumber = `0${digits}`
  }

  if (localNumber.length === 10 && localNumber.startsWith('04')) {
    return `${localNumber.slice(0, 4)} ${localNumber.slice(4, 7)} ${localNumber.slice(7)}`
  }

  return address
}
